import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import CommonDialog from "../CommonDialog/CommonDialog";
import { getViewsByViewType, runValidation } from "../../services/viewService";
import { VIEW_TYPES } from "../../constants/Constants";

export default function ValidatorDialog({ open, onClose, cellId, stickViewId, isDarkMode }) {
  const [schematicViews, setSchematicViews] = useState([]);
  const [selectedSchematicId, setSelectedSchematicId] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingViews, setFetchingViews] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  // Fetch schematic views for this cell whenever dialog opens
  useEffect(() => {
    if (!open || !cellId) return;

    const fetchSchematicViews = async () => {
      setFetchingViews(true);
      setReport(null);
      setError("");
      setSelectedSchematicId("");

      try {
        console.log("Hitting");
        const response = await getViewsByViewType(cellId, [VIEW_TYPES.schematic]);
        if (!response.success) {
          setError("Failed to load schematic views.");
          return;
        }
        setSchematicViews(response.body || []);
      } catch (err) {
        setError("Failed to load schematic views.");
      } finally {
        setFetchingViews(false);
      }
    };

    fetchSchematicViews();
  }, [open, cellId]);

  const handleCompare = async () => {
    if (!selectedSchematicId) return;

    setLoading(true);
    setReport(null);
    setError("");

    try {
      const response = await runValidation(selectedSchematicId, stickViewId);
      if (!response.success) {
        setError("LVS comparison failed.");
        return;
      }
      setReport(response.body.data);
    } catch (err) {
      setError("LVS comparison failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReport(null);
    setError("");
    setSelectedSchematicId("");
    onClose();
  };

  const reportBg = isDarkMode ? "#1e1e1e" : "#f5f5f5";
  const reportColor = report?.clean ? "#4caf50" : "#f44336";

  return (
    <CommonDialog
      open={open}
      onClose={handleClose}
      title="Validation — Stick to Schematic"
      hideBackDrop={false}
      width="600px"
    >
      <>
        {/* Schematic view dropdown */}
        <FormControl fullWidth margin="normal" disabled={fetchingViews}>
          <InputLabel>Select Schematic View</InputLabel>
          <Select
            value={selectedSchematicId}
            onChange={(e) => {
              setSelectedSchematicId(e.target.value);
              setReport(null);
              setError("");
            }}
            label="Select Schematic View"
          >
            {fetchingViews ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
              </MenuItem>
            ) : schematicViews.length === 0 ? (
              <MenuItem disabled>No schematic views found for this cell</MenuItem>
            ) : (
              schematicViews.map((v) => (
                <MenuItem key={v.view_id} value={v.view_id}>
                  {v.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Error */}
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        {/* Report */}
        {report && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: reportColor, fontWeight: 700, mb: 1 }}
            >
              {report.clean ? "✓ VALIDATION CLEAN" : "✗ VALIDATION FAILED"}
              {!report.clean &&
                ` — ${report.structured?.counts?.errors ?? 0} error(s), ${report.structured?.counts?.warnings ?? 0} warning(s)`}
            </Typography>
            <Box
              component="pre"
              sx={{
                backgroundColor: reportBg,
                color: isDarkMode ? "#ffffff" : "#000000",
                fontSize: "11px",
                fontFamily: "monospace",
                p: 1.5,
                borderRadius: 1,
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: "380px",
                whiteSpace: "pre",
              }}
            >
              {report.summary}
            </Box>
          </Box>
        )}
      </>

      {/* Dialog actions */}
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button variant="contained" className="customButtonStyle" onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="contained"
          className="customButtonStyle"
          onClick={handleCompare}
          disabled={!selectedSchematicId || loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {loading ? "Comparing..." : "Compare"}
        </Button>
      </Box>
    </CommonDialog>
  );
}